import { useTranslation } from 'react-i18next'
import { Separator } from '@/components/ui/separator'
import { ShieldCheck } from 'lucide-react'

// Inline SVG payment brand icons for clean, consistent rendering
function VisaIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 780 500" className={className} aria-label="Visa">
      <rect width="780" height="500" rx="40" fill="#1a1f71" />
      <path d="M293.2 348.7l33.4-195.8h53.4l-33.4 195.8h-53.4zm246.8-191c-10.6-4-27.2-8.3-47.9-8.3-52.8 0-90 26.6-90.2 64.7-.3 28.2 26.6 43.9 46.9 53.3 20.8 9.6 27.8 15.8 27.7 24.4-.1 13.2-16.6 19.2-32 19.2-21.4 0-32.7-3-50.3-10.2l-6.9-3.1-7.5 43.8c12.5 5.5 35.6 10.2 59.6 10.5 56.2 0 92.6-26.3 93-66.9.2-22.3-14-39.3-44.8-53.3-18.6-9.1-30-15.1-29.9-24.3 0-8.1 9.6-16.8 30.4-16.8 17.4-.3 30 3.5 39.8 7.5l4.8 2.3 7.2-42.7h.1zm131.4-4.8h-41.3c-12.8 0-22.4 3.5-28 16.3l-79.4 179.5h56.2s9.2-24.1 11.2-29.4h68.6c1.6 6.9 6.5 29.4 6.5 29.4h49.7l-43.5-195.8zm-65.8 126.4c4.4-11.3 21.4-54.8 21.4-54.8-.3.5 4.4-11.4 7.1-18.8l3.6 17s10.3 47 12.5 56.6h-44.6zM327.1 152.9l-52.3 133.6-5.6-27.1c-9.7-31.2-39.9-65-73.7-81.9l47.9 171.1h56.6l84.2-195.8h-57.1z" fill="#fff" />
      <path d="M220.6 152.9h-86.2l-.7 4c67.1 16.2 111.5 55.4 129.9 102.5l-18.7-90.1c-3.2-12.4-12.8-16-24.3-16.4z" fill="#f9a533" />
    </svg>
  )
}

function MastercardIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 780 500" className={className} aria-label="Mastercard">
      <rect width="780" height="500" rx="40" fill="#16366f" />
      <circle cx="330" cy="250" r="150" fill="#eb001b" />
      <circle cx="450" cy="250" r="150" fill="#f79e1b" />
      <path d="M390 130.7c-35.9 28.4-58.9 72.3-58.9 121.3s23 92.9 58.9 121.3c35.9-28.4 58.9-72.3 58.9-121.3s-23-92.9-58.9-121.3z" fill="#ff5f00" />
    </svg>
  )
}

function MaestroIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 780 500" className={className} aria-label="Maestro">
      <rect width="780" height="500" rx="40" fill="#fff" />
      <circle cx="330" cy="250" r="150" fill="#0099df" />
      <circle cx="450" cy="250" r="150" fill="#000" />
      <path d="M390 130.7c-35.9 28.4-58.9 72.3-58.9 121.3s23 92.9 58.9 121.3c35.9-28.4 58.9-72.3 58.9-121.3s-23-92.9-58.9-121.3z" fill="#00588b" />
    </svg>
  )
}

export function Footer() {
  const { t } = useTranslation(['common'])
  const currentYear = new Date().getFullYear()

  return (
    <footer className="w-full border-t bg-card/50 mt-auto">
      <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4">
          {/* Legal links */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 justify-center text-xs">
            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
              {t('legal.termsOfService')}
            </a>
            <Separator orientation="vertical" className="h-3 hidden sm:block" />
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
              {t('legal.privacyPolicy')}
            </a>
            <Separator orientation="vertical" className="h-3 hidden sm:block" />
            <a href="/return-and-cancel-policy" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
              {t('legal.returnAndCancelPolicy')}
            </a>
            <Separator orientation="vertical" className="h-3 hidden sm:block" />
            <a href="/cookie-policy" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
              {t('legal.cookiePolicy')}
            </a>
          </div>

          {/* Payment icons */}
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {/* Card brand icons (mandatory per Piraeus manual Section 8) */}
            <div className="flex items-center gap-1.5 rounded border bg-background px-2 py-1" title="Visa">
              <VisaIcon className="h-5 w-auto" />
            </div>
            <div className="flex items-center gap-1.5 rounded border bg-background px-2 py-1" title="Mastercard">
              <MastercardIcon className="h-5 w-auto" />
            </div>
            <div className="flex items-center gap-1.5 rounded border bg-background px-2 py-1" title="Maestro">
              <MaestroIcon className="h-5 w-auto" />
            </div>

            <Separator orientation="vertical" className="h-5 hidden sm:block" />

            {/* 3D Secure icons (mandatory per Piraeus manual Section 8) */}
            <div className="flex items-center gap-1 text-xs text-muted-foreground rounded border bg-background px-2 py-1" title="Visa Secure - 3D Secure Authentication">
              <ShieldCheck className="h-3.5 w-3.5 text-[#1a1f71]" />
              <span>Visa Secure</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground rounded border bg-background px-2 py-1" title="Mastercard Identity Check - 3D Secure Authentication">
              <ShieldCheck className="h-3.5 w-3.5 text-[#eb001b]" />
              <span>Mastercard Identity Check</span>
            </div>

            <Separator orientation="vertical" className="h-5 hidden sm:block" />

            {/* epay logo (optional) */}
            <img src="/images/piraeus-yellow.png" alt="epay by Piraeus" className="h-6 object-contain opacity-70" />
          </div>

          {/* Copyright */}
          <p className="text-xs text-muted-foreground text-center">
            {t('footer.copyright', { year: currentYear })}
          </p>
        </div>
      </div>
    </footer>
  )
}
